import { GridColDef } from '@mui/x-data-grid';
import { parsedValueToString, parseScan } from './parseScan';
import {
  CompareModeEnum,
  useCompareStore,
} from '../CompareControls/useCompareStore';
import { useMemo } from 'react';
import { Tooltip } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { MetricKeyEnum, metrics } from '../LayerControls/layerStore';

interface TableValue {
  id: string;
  name: string;
  left: string | undefined | null;
  right: string | undefined | null;
  description: string | undefined | null;
}

export const useTableData = () => {
  const { leftScan, rightScan, compareMode } = useCompareStore();

  const columns = useMemo(() => {
    const columns: GridColDef[] = [
      {
        field: 'description',
        headerName: '',
        width: 12,
        hideSortIcons: true,
        sortable: false,
        align: 'center',
        renderCell: (params) =>
          params.value ? (
            <Tooltip title={params.value} enterDelay={300}>
              <InfoOutlined fontSize='small' />
            </Tooltip>
          ) : null,
      },
      {
        field: 'name',
        hideSortIcons: true,
        sortable: false,
        headerName: 'Metric',
        flex: 1,
      },
      {
        field: 'left',
        hideSortIcons: true,
        sortable: false,
        headerName: leftScan?.id?.toString() || '',
        width: 75,
      },
    ];

    if (compareMode === CompareModeEnum.Dual) {
      columns.push({
        field: 'right',
        hideSortIcons: true,
        sortable: false,
        headerName: rightScan?.id?.toString() || '',
        width: 75,
      });
    } else {
      return columns.filter((column) => column.field !== 'right');
    }

    return columns;
  }, [compareMode, leftScan, rightScan]);

  const data = useMemo(() => {
    try {
      const leftValues = parseScan(leftScan);
      const rightValues = parseScan(rightScan);

      const result: TableValue[] = [];

      if (!leftValues) {
        return [];
      }

      Object.keys(leftValues).forEach((key) => {
        const leftValue = leftValues[key as keyof typeof leftValues];

        const rightValue = rightValues?.[key as keyof typeof rightValues];

        result.push({
          id: key,
          name: metrics?.[key as MetricKeyEnum]?.tableName || key,
          description: metrics?.[key as MetricKeyEnum]?.description || '',
          left: parsedValueToString(leftValue),
          right: parsedValueToString(rightValue),
        });
      });

      return result;
    } catch (error) {
      console.error(error);
      return [];
    }
  }, [leftScan, rightScan]);

  return {
    data: data,
    columns: columns,
  };
};
